import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { systemOptions } from '../data/mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', system: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftRef, leftVisible] = useScrollAnimation(0.1);
  const [rightRef, rightVisible] = useScrollAnimation(0.1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success('Message sent! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', company: '', system: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 lg:py-40 bg-[#0c0c10]">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      {/* Glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[150px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Text */}
          <div
            ref={leftRef}
            className={`transition-all duration-700 ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-purple-400 text-sm font-medium tracking-widest uppercase mb-4">Contact</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white mb-6">
              Ready to engineer your <span className="text-gradient">operating system</span>?
            </h2>
            <p className="text-white/50 text-lg mb-8 leading-relaxed">
              Tell us what's broken. We'll map the system and build the fix.
            </p>
            <div className="flex items-center gap-3 text-white/30 text-sm">
              <span className="w-2 h-2 bg-green-400 rounded-full" />
              Typical response time: 2-3 seconds via AI assistant
            </div>
          </div>

          {/* Right - Form */}
          <div
            ref={rightRef}
            className={`transition-all duration-700 delay-200 ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">Name</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#050508] border-white/10 text-white h-12 focus:border-purple-500/50"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">Email</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#050508] border-white/10 text-white h-12 focus:border-purple-500/50"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">Company</label>
                  <Input
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="bg-[#050508] border-white/10 text-white h-12 focus:border-purple-500/50"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">System Type</label>
                  <Select value={formData.system} onValueChange={(v) => setFormData({ ...formData, system: v })}>
                    <SelectTrigger className="bg-[#050508] border-white/10 text-white h-12 focus:ring-purple-500/50">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0c0c10] border-white/10">
                      {systemOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white/70 hover:bg-white/5 focus:bg-white/5">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">Message</label>
                <Textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-[#050508] border-white/10 text-white min-h-[120px] focus:border-purple-500/50 resize-none"
                  placeholder="Tell us about your challenge..."
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
